export interface SolutionStep {
  title: string;
  desc: string;
}

export interface ProblemItem {
  id: string;
  icon: string;
  keyword: string;
  title: string;
  brief: string;
  steps: SolutionStep[];
}

export const problems: ProblemItem[] = [
  {
    id: "cant-open",
    icon: "ri-close-circle-line",
    keyword: "打不开",
    title: "网页打不开",
    brief: "输入网址或点击书签后页面一直空白、转圈或提示无法访问。",
    steps: [
      {
        title: "先确认网络是否正常",
        desc: "试着打开其他网站，如果能正常打开，说明是你的网络或浏览器与笔笔漫画网之间出现了连接问题；如果其他网站也打不开，请先恢复网络。",
      },
      {
        title: "切换网络环境",
        desc: "关闭 Wi-Fi 改用移动数据，或反之。部分运营商对漫画站访问不稳定，换一个网络往往立刻恢复正常。",
      },
      {
        title: "清理缓存与 Cookie",
        desc: "在浏览器设置中清除该网站的缓存和 Cookie，然后刷新页面。旧缓存可能导致加载出错或停留在失效页面。",
      },
      {
        title: "尝试更换浏览器",
        desc: "若当前浏览器持续打不开，可尝试 Chrome、Edge、Safari 等主流浏览器；部分安全插件或广告拦截器可能误拦截页面，可临时关闭后再试。",
      },
    ],
  },
  {
    id: "slow-loading",
    icon: "ri-speed-line",
    keyword: "加载慢",
    title: "加载速度慢",
    brief: "封面图和章节一直加载不出来，翻页卡顿，等待时间过长。",
    steps: [
      {
        title: "检查当前网络速率",
        desc: "打开章节前先确认网络信号。公共 Wi-Fi 或弱网环境下，漫画图片加载会很慢，建议切换到信号更好的网络。",
      },
      {
        title: "开启图片质量调节",
        desc: "在阅读页把画质切换为标准或流畅模式，能明显提升加载速度。高清原图会占用更多带宽，弱网时优先用流畅模式。",
      },
      {
        title: "避开高峰时段",
        desc: "晚间黄金时段访问人数多，加载会偏慢。可以稍等片刻再试，或使用缓存功能提前加载想看的章节。",
      },
      {
        title: "更新浏览器或应用版本",
        desc: "使用过旧的浏览器版本访问现代网页会明显变慢，请及时更新到最新版本。",
      },
    ],
  },
  {
    id: "source-switch",
    icon: "ri-swap-line",
    keyword: "换源失败",
    title: "换源失败",
    brief: "当前漫画源加载异常或内容不完整，切换其他源却一直失败。",
    steps: [
      {
        title: "换源前先刷新页面",
        desc: "换源失败很多时候是页面状态过期导致，先刷新当前漫画详情页，再重新点击换源按钮。",
      },
      {
        title: "尝试不同的源",
        desc: "一部漫画通常有多个源，当前源异常时可依次切换到其他源，多数情况下总有一个可正常加载。",
      },
      {
        title: "清除阅读缓存",
        desc: "在设置里清理已缓存的漫画章节，再返回详情页换源，能避免旧缓存干扰新源加载。",
      },
      {
        title: "换个时间段再换源",
        desc: "个别源在维护期或高峰时可能暂时不可用，过一会儿或换个网络再换源往往就能成功。",
      },
    ],
  },
  {
    id: "favorites",
    icon: "ri-heart-line",
    keyword: "收藏",
    title: "收藏丢失",
    brief: "登录后收藏夹为空，或之前收藏的漫画突然不见了。",
    steps: [
      {
        title: "确认登录账号是否一致",
        desc: "收藏内容与账号绑定。请确认当前登录的账号，与你添加收藏时是同一个，避免误登了其他账号。",
      },
      {
        title: "刷新收藏列表",
        desc: "收藏加载偶尔会出现延迟，下拉刷新或重新进入收藏页，通常即可恢复显示。",
      },
      {
        title: "同步与恢复",
        desc: "在设置中打开收藏自动同步，等待同步完成后再次查看。若刚换过设备，登录后稍等片刻让收藏数据拉取完成。",
      },
      {
        title: "重新添加一次",
        desc: "如果个别漫画确实未显示，可回到漫画详情页重新收藏一次，并在收藏夹中确认是否恢复。",
      },
    ],
  },
  {
    id: "search",
    icon: "ri-search-line",
    keyword: "搜索",
    title: "搜索无结果",
    brief: "输入漫画名或关键词后没有任何结果，搜不到想看的作品。",
    steps: [
      {
        title: "检查关键词拼写",
        desc: "请确认漫画名称的写法是否准确，简体字、繁体字或空格差异都可能导致搜索不到，建议尝试用主角名或别名搜索。",
      },
      {
        title: "尝试精简关键词",
        desc: "把完整标题缩减到核心词，比如只输入主角名或漫画的关键字，命中率更高。",
      },
      {
        title: "清除搜索缓存",
        desc: "部分浏览器会自动补全旧搜索词，清除网站缓存后重新搜索，可避免命中过期结果。",
      },
      {
        title: "换个源或类别查找",
        desc: "某些漫画仅存在于特定源，可切换源后再搜索，或在分类页中按类型逐页浏览查找。",
      },
    ],
  },
  {
    id: "login",
    icon: "ri-user-line",
    keyword: "登录",
    title: "登录异常",
    brief: "验证码收不到、密码错误、登录后闪退或自动退出登录。",
    steps: [
      {
        title: "验证码收不到",
        desc: "检查手机或邮箱的拦截/垃圾箱，确认填写的账号无误后重发一次。多次未收到可等待片刻再尝试。",
      },
      {
        title: "忘记密码",
        desc: "通过找回密码入口重置，重置后使用新密码登录。若仍提示密码错误，注意大小写与空格。",
      },
      {
        title: "登录后自动退出",
        desc: "多为浏览器清理了本地登录状态。关闭浏览器的隐私清理插件，或重新登录并勾选「保持登录」。",
      },
      {
        title: "多次失败被限制",
        desc: "连续输错密码会触发短时限制，稍等几分钟再试即可，无需更换账号。",
      },
    ],
  },
];